/* eslint-disable camelcase */
import { useState, useEffect } from "react";
import {
  Banner,
  Button,
  Card,
  Link,
  TextField,
  TextFieldInput,
  TextFieldLabel,
} from "@hireart/component-library";
import $ from "jquery";
import PropTypes from "prop-types";
import queryString from "query-string";
import PhoneInput from "react-phone-number-input";
import { connect } from "react-redux";

import { getMetaContent } from "../../Common/helpers/meta";
import { identifyNamedPathFromUrl } from "../../Common/helpers/utils";
import managerOnboardingText from "../helpers";

import { FormError } from "./FormError";

const Registration = ({ flashMessage, location }) => {
  const [user, setUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    phone_number: "",
  });
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [responsibilities, setResponsibilities] = useState([]);
  const [invalidSetupUrl, setInvalidSetupUrl] = useState(false);
  const [redirectLocation, setRedirectLocation] = useState(null);

  useEffect(() => {
    const { reset_password_token, redirect_url } = queryString.parse(
      location.search
    );

    if (!reset_password_token) {
      setInvalidSetupUrl(true);
      setInitialLoad(false);
      return;
    }

    $.ajax({
      url: "/users/password/edit",
      type: "GET",
      dataType: "json",
      data: { reset_password_token, redirect_url },
    })
      .then(
        ({
          email,
          last_name,
          first_name,
          phone_number,
          user_root_path,
          pending_setup,
          responsibilities: fetchedResponsibilities,
        }) => {
          const parsedUrl = queryString.parse(location.search);
          // Delete the redirectUrl query params so we do not include this when redirecting later
          delete parsedUrl.redirect_url;
          const newRedirectLocation = redirect_url
            ? `${redirect_url}?${queryString.stringify(parsedUrl)}`
            : user_root_path;

          if (!pending_setup) {
            window.location.href = newRedirectLocation;
            return;
          }

          setUser((prev) => ({
            ...prev,
            email,
            first_name: first_name || "",
            last_name: last_name || "",
            phone_number: phone_number || "",
          }));
          setError("");
          setInitialLoad(false);
          setRedirectLocation(newRedirectLocation);
          setResponsibilities(fetchedResponsibilities);
        }
      )
      .catch(() => {
        setInvalidSetupUrl(true);
        setInitialLoad(false);
      });
    // Intentionally run once on mount (mirrors componentDidMount) — location.search
    // is stable for the lifetime of this route and we must not re-fetch on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name === "password_confirmation" ? "password" : name]: [],
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    const csrfToken = getMetaContent("csrf-token");

    const { reset_password_token } = queryString.parse(location.search);

    return $.ajax({
      url: "/user_account_update",
      type: "PUT",
      dataType: "json",
      data: { user: { ...user }, token: reset_password_token },
      beforeSend: (request) =>
        request.setRequestHeader("X-CSRF-Token", csrfToken),
      success: () => {
        setError([]);
        setLoading(false);
        window.location.href = redirectLocation;
      },
      error: (xhr) => {
        const errorList = [];
        const { errors: responseErrors } = JSON.parse(xhr.responseText);

        if (typeof responseErrors === "string") {
          errorList.push(responseErrors);
        } else {
          const { password, password_confirmation, first_name, last_name } =
            responseErrors;
          if (first_name) {
            first_name.forEach((err) => errorList.push(`First name ${err}`));
          }
          if (last_name) {
            last_name.forEach((err) => errorList.push(`Last name ${err}`));
          }
          if (password) {
            password.forEach((err) =>
              err.match(/passwords/gi)
                ? errorList.push(err)
                : errorList.push(`Password ${err}`)
            );
          }
          if (password_confirmation) {
            password_confirmation.forEach((err) =>
              errorList.push(`Password confirmation ${err}`)
            );
          }
        }

        setLoading(false);
        setError(errorList);
        setErrors(responseErrors);
      },
    });
  };

  const validateFormField = () => {
    return Object.keys(user)
      .filter((k) => k !== "phone_number")
      .some((k) => user[k].trim().length === 0);
  };

  const setupContextText = (email = null) => {
    const { redirect_url } = queryString.parse(location.search);
    const namedPath = identifyNamedPathFromUrl(redirect_url);

    switch (namedPath) {
      case "jobs_onboarding":
      case "candidates_offers":
        return `To continue, first set up your account for ${email}`;
      default:
        return managerOnboardingText(responsibilities, email);
    }
  };

  const {
    first_name,
    last_name,
    password,
    password_confirmation,
    phone_number,
    email,
  } = user;
  const setupTextContext = setupContextText(email);

  if (initialLoad) {
    return (
      <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
        <p>Loading...</p>
      </Card>
    );
  }

  if (invalidSetupUrl) {
    return (
      <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
        <h2 className="headline-1 mb-24 text-center">Welcome to HireArt.</h2>
        <p className="mb-16">
          This account registration link is invalid or has expired.
        </p>
        <p>
          Tip: To access your onboarding, log in to your{" "}
          <Link to="https://app.hireart.com/candidates">HireArt dashboard</Link>.
          After signing in, you&rsquo;ll find your offer on the left-hand side
          under &lsquo;Offers.&rsquo; Use your email address as your username,
          and select &lsquo;Forgot Password&rsquo; on the login screen if you
          need to set (or reset) your password.
        </p>
      </Card>
    );
  }

  return (
    <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
      <h2 className="headline-1 mb-24 text-center">Welcome to HireArt.</h2>
      <p className="mb-16">{setupTextContext}</p>

      {!error.length && flashMessage && flashMessage.length > 0 && !loading && (
        <Banner className="mb-16" variant="warning">
          {flashMessage}
        </Banner>
      )}

      <FormError error={error} />

      <form onSubmit={handleSubmit}>
        <div className="my-16">
          <TextField hasError={errors.first_name?.length > 0}>
            <TextFieldLabel htmlFor="first_name" isUnstyled>
              First name
            </TextFieldLabel>
            <TextFieldInput
              autoFocus={first_name.length === 0}
              id="first_name"
              name="first_name"
              onChange={handleInputChange}
              placeholder="First name"
              size="md"
              tabIndex="0"
              value={first_name}
            />
          </TextField>
        </div>

        <div className="my-16">
          <TextField hasError={errors.last_name?.length > 0}>
            <TextFieldLabel htmlFor="last_name" isUnstyled>
              Last name
            </TextFieldLabel>
            <TextFieldInput
              id="last_name"
              name="last_name"
              onChange={handleInputChange}
              placeholder="Last name"
              size="md"
              tabIndex="0"
              value={last_name}
            />
          </TextField>
        </div>

        <div className="flex flex-col flex-nowrap">
          <PhoneInput
            tabIndex="0"
            country="US"
            autoComplete="tel"
            type="tel"
            showCountrySelect={false}
            value={phone_number}
            onChange={(phone) => {
              setUser((prev) => ({ ...prev, phone_number: phone || "" }));
            }}
            name="phone_number"
            placeholder="Phone number (optional)"
            autoFocus={phone_number.length === 0 && first_name.length > 0}
          />

          <div>
            <TextField hasError={errors.password?.length > 0}>
              <TextFieldLabel htmlFor="password" isUnstyled>
                Create password
              </TextFieldLabel>
              <TextFieldInput
                autoFocus={phone_number.length > 0}
                id="password"
                name="password"
                onChange={handleInputChange}
                placeholder="Create password"
                size="md"
                tabIndex="0"
                type="password"
                value={password}
              />
            </TextField>
          </div>
        </div>

        <input
          tabIndex="-1"
          onChange={() => {}}
          name="email"
          type="email"
          value={email}
          readOnly
          hidden
        />

        <div className="my-16">
          <TextField hasError={errors.password?.length > 0}>
            <TextFieldLabel htmlFor="password_confirmation" isUnstyled>
              Confirm password
            </TextFieldLabel>
            <TextFieldInput
              id="password_confirmation"
              name="password_confirmation"
              onChange={handleInputChange}
              placeholder="Confirm password"
              size="md"
              tabIndex="0"
              type="password"
              value={password_confirmation}
            />
          </TextField>
        </div>

        <Button
          isLoading={loading}
          type="submit"
          isFullWidth
          isDisabled={validateFormField() || loading}
        >
          Create my account
        </Button>
      </form>
    </Card>
  );
};

Registration.propTypes = {
  flashMessage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
};

Registration.defaultProps = {
  flashMessage: null,
};

const ConnectedRegistration = connect(({ session: { flash } }) => ({
  flashMessage: flash,
}))(Registration);

export { ConnectedRegistration as Registration };
